/**
 * `mockups/client/files.html` §5 — delegated access, masked, revealed once.
 *
 * The list is labels and masks (`GET /api/clients/me/credentials`); the plaintext
 * is a **mutation** (`POST …/:id/reveal`), and that is not a REST accident. It
 * writes an audit row, and a secret that lands in a cache is a secret at rest in a
 * tab somebody left open — so it is asked for on purpose, shown once, and dropped
 * when the dialog closes **or** when this panel goes away, whichever happens first.
 *
 * **Owner and admin only, and never under impersonation.**
 * `clients.service.revealCredential` refuses everybody else with
 * `assertAccountAdmin` and refuses an impersonating staff member with
 * `assertNotImpersonating` — recording `CREDENTIAL_REVEAL_REFUSED_IMPERSONATION`
 * durably, outside the request's own transaction, because the 403 rolls that
 * transaction back. Offering the button to a caller the API answers with a
 * security audit row is the opposite of what
 * `.docs/19-client-portal-pages.md` §Edge cases asks for: a control that is never
 * allowed is not disabled, it is absent.
 *
 * The check is written out here rather than borrowed from `DecisionCard`'s
 * `mayApprove`. They are the same three rules today and they are deliberately
 * different ones: `clients/service.ts` says so at length — "revealing a password
 * is not approving a payment… no coupling that would silently change who may see
 * a credential the day the money rule changes."
 *
 * The credential list is fetched **here** rather than by `FilesPage`, so the two
 * views that never show it do not ask for it. It is one panel behind one segment
 * of a segmented control, and `RequestComposer` fetches its own catalogue for the
 * same reason.
 */

import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { useCredentials, useRevealCredential } from '../../../api/client/index.js'
import type { Principal } from '../../../auth/principal.js'
import { usePrincipal, isAccountAdmin, isImpersonating } from '../../../auth/principal.js'
import { Button, ButtonRow } from '../../../components/portal/Button.js'
import { Card, CardBody, CardHead, CardTitle } from '../../../components/portal/Card.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { Kv, KvRow } from '../../../components/portal/Kv.js'
import { FileRow, Mask } from '../../../components/portal/Files.js'
import { Stack } from '../../../components/portal/Layout.js'
import { Dialog } from '../../../components/portal/Overlay.js'
import { Notice } from '../../../components/portal/Status.js'
import { DateText, Mono, Prose } from '../../../components/portal/Text.js'
// Whether the last copy reached the clipboard is null until somebody asks; the copy
// itself, and why its rejection is never reported, is the integration's.
import { copyToClipboard, type CopyState } from '../../../integrations/clipboard.js'

type CredentialsBody = NonNullable<ReturnType<typeof useCredentials>['data']>

export type CredentialRow = CredentialsBody['credentials'][number]

/** Owner or admin, or studio staff — and nobody who is impersonating. */
export function mayRevealCredential(principal: Principal): boolean {
  /*
   * Three rules, and the impersonation one is the whole reason this is a function.
   *
   * `isAccountAdmin` is **true** for an impersonating staff member: impersonation
   * is a client session (`auth/principal.tsx`), the role is the target's own, and
   * that function's docstring says in as many words that it is not the money rule
   * and does not refuse one. Reading a member list is not a decision; reading
   * somebody's Search Console password while wearing their name is.
   *
   * The staff half below is kept for the same reason it always was: a staff member
   * holds no seat and `guardClientPortal` sends them to their own portal, so in
   * practice this panel never renders for one — but the page being stricter than
   * the door it calls would be the wrong kind of wrong.
   */
  if (isImpersonating(principal)) return false
  return principal.kind === 'staff_user' || isAccountAdmin(principal)
}

/**
 * What the revealed value is called.
 *
 * `AddCredentialSchema.secret` is "the password, key or token" — three things a
 * studio holds delegated access with, and one label has to cover all three without
 * claiming to know which this one is. It is a constant because the heading, the
 * button and both status sentences are the same noun, and three spellings of it in
 * one dialog is how a reader ends up wondering whether they are three fields.
 */
const SECRET_LABEL = 'Password or key'

export function DelegatedAccess() {
  const principal = usePrincipal()
  const mayReveal = mayRevealCredential(principal)
  const queryClient = useQueryClient()
  const credentials = useCredentials()
  const reveal = useRevealCredential()
  const [selected, setSelected] = useState<CredentialRow | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [copied, setCopied] = useState<CopyState | null>(null)

  /**
   * Which credentials this panel has revealed, so their mutations can be found
   * again. A ref rather than state: nothing renders from it.
   */
  const revealed = useRef<Set<string>>(new Set())

  function close() {
    setSelected(null)
    setSecret(null)
    reveal.reset()
    forgetRevealed(queryClient, revealed.current)
  }

  /*
   * Unmounting is not closing, and this is the case the dialog's own *Done* button
   * does not cover: clicking another segment of the seg takes `DelegatedAccess`
   * off the tree with the dialog still open, so `close()` never runs.
   *
   * `reveal.reset()` would not be enough here even if it did run. It detaches
   * *this observer* from the mutation — `reveal.data` becomes undefined — and then
   * schedules the mutation for collection, five minutes later
   * (`query-core` `Removable#scheduleGc`, `gcTime` defaulting to 5 × 60 × 1000).
   * Until that timer fires the plaintext is still sitting in
   * `queryClient.getMutationCache()`, which is the tab-left-open case this dialog
   * exists to avoid. So the row is removed rather than left to a timer.
   */
  useEffect(
    () => () => {
      forgetRevealed(queryClient, revealed.current)
    },
    [queryClient],
  )

  const rows = credentials.data?.credentials ?? []

  return (
    <Card>
      <CardHead>
        <CardTitle>Delegated access</CardTitle>
      </CardHead>

      {credentials.isError ? (
        /*
         * `useCredentials` is `inline`, so a refusal lands here rather than on the
         * route's error boundary — the point being that a client who may not read
         * this list still gets their deliverables. Without a branch for it the
         * panel said "Loading your delegated access…" for ever.
         */
        <CardBody>
          <Empty>{credentials.error.message}</Empty>
        </CardBody>
      ) : credentials.data === undefined ? (
        <CardBody>
          <Empty>Loading your delegated access…</Empty>
        </CardBody>
      ) : rows.length === 0 ? (
        <CardBody>
          <Empty>You have not given us access to anything.</Empty>
        </CardBody>
      ) : (
        <CardBody tight>
          {rows.map((credential) => (
            <FileRow
              key={credential.id}
              icon={credential.kind}
              title={credential.label}
              sub={
                <>
                  {credential.revokedAt === null ? 'Granted ' : 'Revoked '}
                  <DateText value={credential.revokedAt ?? credential.grantedAt} nowrap /> ·{' '}
                  <Mask>{credential.identifierMasked}</Mask>
                </>
              }
              meta={
                mayReveal && credential.revokedAt === null ? (
                  <Button
                    size="sm"
                    aria-label={`Reveal ${credential.label}`}
                    onClick={() => {
                      reveal.reset()
                      setSecret(null)
                      setCopied(null)
                      setSelected(credential)
                    }}
                  >
                    Reveal
                  </Button>
                ) : null
              }
            />
          ))}
        </CardBody>
      )}

      <Dialog
        open={selected !== null}
        onOpenChange={(next) => {
          if (!next) close()
        }}
        title={selected === null ? '' : selected.label}
        modalOnly
      >
        {selected === null ? null : secret === null ? (
          <Stack>
            <Prose>
              We will show it once, here. The studio records that you asked and when — that record
              is the point of the button.
            </Prose>
            {reveal.isError ? <Notice role="alert">{reveal.error.message}</Notice> : null}
            <ButtonRow>
              <Button
                variant="ochre"
                disabled={reveal.isPending}
                onClick={() => {
                  const id = selected.id
                  revealed.current.add(id)
                  reveal.mutate(id, {
                    onSuccess: (answer) => {
                      setSecret(answer.secret)
                    },
                  })
                }}
              >
                Reveal {selected.label}
              </Button>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
            </ButtonRow>
          </Stack>
        ) : (
          <Stack>
            {/*
              R3-28, and it is two findings in one row.

              **The value had no label.** The row above reads `Search Console ·
              seo@••••••••••••t.com`, and *Reveal* replaced the panel with
              `gsc-delegated` under the same heading — so it read as a *failed*
              unmasking of the address rather than as a different field. They are two
              columns (`identifier_masked` and `secret_enc`) and the reveal returns
              only the second (`clients.service.revealCredential`), so the two are
              named and the identifier stays masked, which is all we hold in the clear.

              **And "Copy it now" offered nothing to copy it with.** The button below
              is `navigator.clipboard.writeText` and nothing else: no
              `document.execCommand` fallback, because that one needs a node holding
              the plaintext and a selection over it, which is more of the secret in
              more of the page than the one `<Mono>` it is already in. Where the API
              is missing the panel says so and leaves the value to be selected by
              hand. Nothing is logged, here or in the failure branch — the rejection
              is discarded rather than reported, because a clipboard error can carry
              what it was asked to write.
            */}
            <Kv bare>
              <KvRow k="Account" v={<Mask>{selected.identifierMasked}</Mask>} />
              <KvRow k={SECRET_LABEL} v={<Mono>{secret}</Mono>} />
            </Kv>
            <Prose>
              Copy it now. Closing this puts it back — it is not kept in this browser, and asking
              again writes another line in the log.
            </Prose>
            {copied === null ? null : (
              <Notice role="status">
                {copied === 'yes'
                  ? `The ${SECRET_LABEL.toLowerCase()} is on your clipboard.`
                  : `This browser did not let us reach the clipboard. Select the ${SECRET_LABEL.toLowerCase()} above and copy it.`}
              </Notice>
            )}
            <ButtonRow>
              <Button
                variant="primary"
                onClick={() => {
                  copyToClipboard(secret, setCopied)
                }}
              >
                Copy the {SECRET_LABEL.toLowerCase()}
              </Button>
              <Button variant="ghost" onClick={close}>
                Done
              </Button>
            </ButtonRow>
          </Stack>
        )}
      </Dialog>
    </Card>
  )
}

/**
 * Take every reveal this panel made out of the mutation cache.
 *
 * A mutation is found by the variables it was called with, which for
 * `useRevealCredential` is the credential's own id — `useApiMutation` sets no
 * `mutationKey`, so there is nothing else to match on, and a uuid is specific
 * enough that this can only ever remove a row this panel wrote.
 *
 * Outside the component because it touches no React state and takes both of the
 * two stable things it needs; that is what lets the unmount effect depend on
 * nothing that changes between renders.
 */
function forgetRevealed(queryClient: QueryClient, revealed: Set<string>): void {
  if (revealed.size === 0) return
  const cache = queryClient.getMutationCache()
  for (const mutation of cache.getAll()) {
    const variables = mutation.state.variables
    if (typeof variables === 'string' && revealed.has(variables)) cache.remove(mutation)
  }
  revealed.clear()
}
